import { Injectable } from '@angular/core';
import { VoiceIntent } from '../models';
import { readCalendarId } from '../handlers/calendar-choice.util';
import { ALLOWED_ENDPOINTS, getEndpointConfig } from '../constants/allowed-endpoints.const';

export interface ValidationResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
}

/**
 * Service de validation des intentions vocales
 * Vérifie que l'intention est valide et sécurisée avant exécution
 */
@Injectable({ providedIn: 'root' })
export class IntentValidatorService {

  /**
   * Valide une intention vocale
   */
  validate(intent: VoiceIntent | null): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    if (!intent) {
      errors.push('Intention vide ou invalide');
      return { valid: false, errors, warnings };
    }

    // Vérifier que l'endpoint est dans la liste blanche
    if (!this.isEndpointAllowed(intent.endpoint)) {
      errors.push(`Endpoint non autorisé: ${intent.endpoint}`);
    }

    // Vérifier la confiance minimum
    const config = getEndpointConfig(intent.endpoint);
    if (config && intent.confidence < config.minConfidence) {
      warnings.push(`Confiance faible (${(intent.confidence * 100).toFixed(0)}%)`);
    }

    // Vérifier les paramètres requis selon l'endpoint
    const paramErrors = this.validateParams(intent);
    errors.push(...paramErrors);

    // Vérifier si une clarification est nécessaire
    if (intent.needsClarification) {
      errors.push('Clarification requise');
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings
    };
  }

  /**
   * Vérifie si l'endpoint est autorisé
   */
  isEndpointAllowed(endpoint: string): boolean {
    return endpoint in ALLOWED_ENDPOINTS;
  }

  /**
   * Vérifie si une confirmation est requise
   */
  requiresConfirmation(intent: VoiceIntent): boolean {
    const config = getEndpointConfig(intent.endpoint);
    return intent.confirmationRequired || (config?.requiresConfirmation ?? false);
  }

  /**
   * Valide les paramètres selon l'endpoint
   */
  private validateParams(intent: VoiceIntent): string[] {
    const errors: string[] = [];
    const params = intent.params;

    switch (intent.endpoint) {
      case 'calendar.addPresences':
      case 'calendar.removePresences':
        if (!params['dates'] || !Array.isArray(params['dates'])) {
          errors.push('Paramètre "dates" requis (tableau)');
        } else if (params['dates'].length === 0) {
          errors.push('Au moins une date est requise');
        } else {
          // Valider le format des dates
          const invalidDates = (params['dates'] as string[]).filter(
            d => !this.isValidDateFormat(d)
          );
          if (invalidDates.length > 0) {
            errors.push(`Dates invalides: ${invalidDates.join(', ')}`);
          }
        }

        // `calendarId` vient d'un bouton de choix (MOKA-407), jamais du modèle.
        if (params['calendarId'] !== undefined && readCalendarId(params) === null) {
          errors.push('Paramètre "calendarId" invalide');
        }

        if (intent.endpoint === 'calendar.addPresences') {
          const validPortions = ['FullDay', 'Morning', 'Afternoon', 'Evening'];
          if (params['portion'] && !validPortions.includes(params['portion'] as string)) {
            errors.push(`Portion invalide: ${params['portion']}`);
          }
        }
        break;

      case 'calendar.listPresences':
        if (!params['startDate'] || !this.isValidDateFormat(params['startDate'] as string)) {
          errors.push('Paramètre "startDate" requis (format YYYY-MM-DD)');
        }
        if (!params['endDate'] || !this.isValidDateFormat(params['endDate'] as string)) {
          errors.push('Paramètre "endDate" requis (format YYYY-MM-DD)');
        }
        break;
        case 'project.createTask':
          if (!params['title'] || typeof params['title'] !== 'string' || !(params['title'] as string).trim()) {
            errors.push('Le titre de la tâche est requis');
          }
          if (params['priority']) {
            const validPriorities = ['low', 'medium', 'high', 'urgent'];
            if (!validPriorities.includes(params['priority'] as string)) {
              errors.push(`Priorité invalide: ${params['priority']}`);
            }
          }
          if (params['dueDate'] && !this.isValidDateFormat(params['dueDate'] as string)) {
            errors.push(`Date limite invalide: ${params['dueDate']}`);
          }
          break;
  
        case 'project.updateTaskStatus':
          if (!params['taskTitle'] || typeof params['taskTitle'] !== 'string' || !(params['taskTitle'] as string).trim()) {
            errors.push('Le titre de la tâche est requis');
          }
          if (!params['status']) {
            errors.push('Le nouveau statut est requis');
          } else {
            const validStatuses = ['todo', 'in_progress', 'in_review', 'done', 'blocked'];
            if (!validStatuses.includes(params['status'] as string)) {
              errors.push(`Statut invalide: ${params['status']}`);
            }
          }
          break;
  
        case 'project.deleteTask':
          if (!params['taskTitle'] || typeof params['taskTitle'] !== 'string' || !(params['taskTitle'] as string).trim()) {
            errors.push('Le titre de la tâche est requis');
          }
          break;

        case 'project.assignTask':
          if (!params['taskTitle'] || typeof params['taskTitle'] !== 'string' || !(params['taskTitle'] as string).trim()) {
            errors.push('Le titre de la tâche est requis');
          }
          if (!params['assigneeName'] || typeof params['assigneeName'] !== 'string' || !(params['assigneeName'] as string).trim()) {
            errors.push('Le nom de la personne à assigner est requis');
          }
          break;

        case 'project.listMembers':
          // `projectName` est optionnel (résolution contextuelle par
          // `resolveProjectId` si absent, cf. `ProjectVoiceHandler.handleListMembers`)
          // — aucun paramètre requis, case explicite pour ne pas laisser croire
          // à un oubli.
          break;

        case 'project.listMyTasks': {
          const validScopes = ['today', 'overdue', 'all'];
          if (!params['scope'] || !validScopes.includes(params['scope'] as string)) {
            errors.push(`Portée invalide ou manquante: ${params['scope'] ?? '(absente)'} (attendu today|overdue|all)`);
          }
          // `projectName` est optionnel — pas de contrainte supplémentaire.
          break;
        }

        case 'project.projectSummary':
          // `projectName` est optionnel (même résolution contextuelle que
          // `project.listMembers`) — aucun paramètre requis.
          break;

        case 'project.addComment':
          if (!params['comment'] || typeof params['comment'] !== 'string' || !(params['comment'] as string).trim()) {
            errors.push('Le contenu du commentaire est requis');
          }
          break;

        case 'project.addTagToTask':
          if (!params['tag'] || typeof params['tag'] !== 'string' || !(params['tag'] as string).trim()) {
            errors.push('Le nom de l\'étiquette est requis');
          }
          break;

        case 'inventory.createClaim':
          if (!params['description'] || typeof params['description'] !== 'string' || !(params['description'] as string).trim()) {
            errors.push('La description de la réclamation est requise');
          }
          break;

        case 'rooms.reserveRoom': {
          if (!params['date'] || !this.isValidDateFormat(params['date'] as string)) {
            errors.push('Paramètre "date" requis (format YYYY-MM-DD)');
          }
          // MOKA-266 : objet de la réunion, optionnel. Absent ou vide → titre par
          // défaut côté handler ; présent, ce doit être du texte raisonnable.
          const title = params['title'];
          if (title !== undefined && title !== null) {
            if (typeof title !== 'string') {
              errors.push('L\'objet de la réunion ("title") doit être un texte');
            } else if (title.length > 500) {
              errors.push('L\'objet de la réunion est trop long (500 caractères au plus)');
            }
          }
          break;
        }

        case 'session.setContext':
          // Tous les champs (person/group/release/dueDate) sont optionnels —
          // no-op front, aucun appel HTTP. Aucun paramètre requis.
          break;
    }

    return errors;
  }

  /**
   * Vérifie le format de date YYYY-MM-DD
   */
  private isValidDateFormat(date: string): boolean {
    if (typeof date !== 'string') return false;
    const regex = /^\d{4}-\d{2}-\d{2}$/;
    if (!regex.test(date)) return false;

    const parsed = new Date(date);
    return !isNaN(parsed.getTime());
  }
}
